import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, RotateCcw, ChevronRight, Shuffle, AlertTriangle } from 'lucide-react';
import { useCourse } from '../hooks/useCourse';
import { useAuth } from '../hooks/useAuth';
import { MathKeyboard } from './MathKeyboard';
import { useProgress } from '../hooks/useProgress';
import { logActivity, getFlashcardProgress } from '../lib/supabase';
import { Flashcard, FlashcardProgress } from '../types';
import { getCourseProgress, getNextRecommendation } from '../lib/recommendations';
import { RecommendationCard } from './RecommendationCard';

// ============================================
// LEVENSHTEIN DISTANCE
// ============================================

function levenshteinDistance(a: string, b: string): number {
  const matrix: number[][] = [];

  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1      // deletion
        );
      }
    }
  }

  return matrix[b.length][a.length];
}

// Normalize answer for flexible comparison
// Handles parentheses by EXTRACTING content, not removing it
// "(schmerzhafter) Stich" → "schmerzhafter stich"
// "Brandmarke(n)" → "brandmarke" or "brandmarken"
function normalizeAnswer(text: string): string {
  return text
    .toLowerCase()
    .trim()
    // Extract content from parentheses (remove the parentheses but keep content)
    // "(schmerzhafter) Stich" → "schmerzhafter Stich"
    .replace(/\(([^)]*)\)/g, '$1')
    // Remove placeholder abbreviations
    .replace(/\bsb\.?\b/g, '')      // "sb" or "sb."
    .replace(/\bsth\.?\b/g, '')     // "sth" or "sth."
    .replace(/\.\.\./g, '')          // "..."
    // Remove remaining punctuation (including en-dash – and em-dash —)
    .replace(/[.,!?;:'"\/\-–—]/g, '')
    // Normalize whitespace
    .replace(/\s+/g, ' ')
    .trim();
}

// Parse answer that may contain multiple options
// Handles parentheses specially:
// "(schmerzhafter) Stich" → ["schmerzhafter stich", "stich"]
// "Brandmarke(n)" → ["brandmarke", "brandmarken"]
function parseAcceptedAnswers(answer: string): string[] {
  const results: string[] = [];
  const lowerAnswer = answer.toLowerCase();

  // Check for pattern like "(content) word" - optional prefix
  const optionalPrefixMatch = lowerAnswer.match(/^\(([^)]+)\)\s*(.+)$/);
  if (optionalPrefixMatch) {
    const optionalPart = optionalPrefixMatch[1];
    const mainPart = optionalPrefixMatch[2];
    // Add version WITH the optional part
    results.push(cleanString(`${optionalPart} ${mainPart}`));
    // Add version WITHOUT the optional part
    results.push(cleanString(mainPart));
  }

  // Check for pattern like "word(suffix)" - optional suffix (e.g., "Brandmarke(n)")
  const optionalSuffixMatch = lowerAnswer.match(/^([^(]+)\(([^)]+)\)$/);
  if (optionalSuffixMatch) {
    const mainPart = optionalSuffixMatch[1];
    const optionalSuffix = optionalSuffixMatch[2];
    // Add version WITHOUT the suffix
    results.push(cleanString(mainPart));
    // Add version WITH the suffix appended
    results.push(cleanString(mainPart + optionalSuffix));
  }

  // Check for pattern like "word (content) word" - optional middle part
  const optionalMiddleMatch = lowerAnswer.match(/^(.+?)\s*\(([^)]+)\)\s*(.+)$/);
  if (optionalMiddleMatch && !optionalPrefixMatch) {
    const before = optionalMiddleMatch[1];
    const optional = optionalMiddleMatch[2];
    const after = optionalMiddleMatch[3];
    // Add version WITH the optional part
    results.push(cleanString(`${before} ${optional} ${after}`));
    // Add version WITHOUT the optional part
    results.push(cleanString(`${before} ${after}`));
  }

  // Always add the fully normalized version (parentheses content extracted)
  const fullyNormalized = normalizeAnswer(answer);
  if (fullyNormalized && !results.includes(fullyNormalized)) {
    results.push(fullyNormalized);
  }

  // Split by common separators: comma, slash, en-dash, em-dash, colon+space, oder, or
  const parts = lowerAnswer.split(/[,\/]|\s*[–—]\s*|\s*:\s+|\s+oder\s+|\s+or\s+/i);
  for (const part of parts) {
    const cleaned = cleanString(part);
    if (cleaned && !results.includes(cleaned)) {
      results.push(cleaned);
    }
    // Also add version with parentheses content extracted
    const extracted = normalizeAnswer(part);
    if (extracted && !results.includes(extracted)) {
      results.push(extracted);
    }
  }

  // For each accepted answer, also add version without leading German article
  const germanArticleRegex = /^(die|der|das|ein|eine|einen|einem|einer)\s+/;
  const withoutArticles: string[] = [];
  for (const answer of results) {
    const stripped = answer.replace(germanArticleRegex, '');
    if (stripped !== answer && stripped.length > 0 && !results.includes(stripped)) {
      withoutArticles.push(stripped);
    }
  }
  results.push(...withoutArticles);

  return results.filter(r => r.length > 0);
}

// Helper to clean a string
function cleanString(text: string): string {
  return text
    .toLowerCase()
    .replace(/\bsb\.?\b/g, '')
    .replace(/\bsth\.?\b/g, '')
    .replace(/\.\.\./g, '')
    .replace(/[.,!?;:'"()\/\-–—]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

type MatchResult = 'correct' | 'almost' | 'wrong';

// Check if strings match with word-based ratio check
// Only allows substring match if shorter has >= 80% of words of longer
function containsMatch(a: string, b: string): boolean {
  if (a.length === 0 || b.length === 0) return false;

  const wordsA = a.split(/\s+/).filter(w => w.length > 0);
  const wordsB = b.split(/\s+/).filter(w => w.length > 0);

  const shorter = wordsA.length <= wordsB.length ? wordsA : wordsB;
  const longer = wordsA.length > wordsB.length ? wordsA : wordsB;

  // Word ratio must be >= 80%
  const ratio = shorter.length / longer.length;
  if (ratio < 0.8) {
    return false;
  }

  // Check if the shorter string is contained in the longer
  const shorterStr = shorter.join(' ');
  const longerStr = longer.join(' ');
  return longerStr.includes(shorterStr);
}

function checkAnswer(userInput: string, correctAnswer: string): {
  result: MatchResult;
  distance: number;
  normalizedInput: string;
  normalizedCorrect: string;
} {
  const normalizedInput = normalizeAnswer(userInput);
  const acceptedAnswers = parseAcceptedAnswers(correctAnswer);

  let bestDistance = Infinity;
  let bestResult: MatchResult = 'wrong';
  let bestNormalizedCorrect = acceptedAnswers[0] || '';

  // GUARD: Very short input (under 3 chars) is always wrong,
  // unless the correct answer itself is that short (e.g. "ob", "an")
  const shortestAccepted = Math.min(...acceptedAnswers.map(a => a.length));
  if (normalizedInput.length < 3 && shortestAccepted >= 3) {
    return { result: 'wrong', distance: 99, normalizedInput, normalizedCorrect: bestNormalizedCorrect };
  }

  for (const accepted of acceptedAnswers) {
    // GUARD: Input must be at least 50% the length of the correct answer
    if (normalizedInput.length < accepted.length * 0.5 && accepted.length >= 4) {
      continue; // Skip this accepted answer, input is way too short
    }

    // Exact match after normalization (case-insensitive already via normalizeAnswer)
    if (normalizedInput === accepted) {
      return { result: 'correct', distance: 0, normalizedInput, normalizedCorrect: accepted };
    }

    // Substring/contains match — only if input is at least 50% length of accepted
    // This prevents short inputs like "i" matching inside long words
    if (normalizedInput.length >= accepted.length * 0.5 && containsMatch(normalizedInput, accepted)) {
      return { result: 'correct', distance: 0, normalizedInput, normalizedCorrect: accepted };
    }

    const distance = levenshteinDistance(normalizedInput, accepted);

    if (distance < bestDistance) {
      bestDistance = distance;
      bestNormalizedCorrect = accepted;

      const lengthRatio = normalizedInput.length / accepted.length;

      // CORRECT: distance ≤ 2 AND input covers ≥ 80% of correct answer's characters
      // ALMOST: distance 3-4 AND input covers ≥ 70% of correct answer length
      // WRONG: everything else
      if (distance <= 2 && lengthRatio >= 0.8) {
        bestResult = 'correct';
      } else if (distance <= 4 && lengthRatio >= 0.7) {
        bestResult = 'almost';
      } else {
        bestResult = 'wrong';
      }
    }
  }

  return { result: bestResult, distance: bestDistance, normalizedInput, normalizedCorrect: bestNormalizedCorrect };
}

// ============================================
// SOUND EFFECTS
// ============================================

const audioContext = typeof window !== 'undefined'
  ? new (window.AudioContext || (window as typeof window & { webkitAudioContext: typeof AudioContext }).webkitAudioContext)()
  : null;

function playSound(type: 'correct' | 'almost' | 'wrong') {
  if (!audioContext) return;
  if (audioContext.state === 'suspended') audioContext.resume();

  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);

  const now = audioContext.currentTime;

  switch (type) {
    case 'correct':
      oscillator.frequency.setValueAtTime(600, now);
      oscillator.frequency.setValueAtTime(800, now + 0.05);
      gainNode.gain.setValueAtTime(0.15, now);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.1);
      oscillator.start(now);
      oscillator.stop(now + 0.1);
      break;
    case 'almost':
      oscillator.frequency.setValueAtTime(500, now);
      oscillator.frequency.setValueAtTime(600, now + 0.1);
      gainNode.gain.setValueAtTime(0.12, now);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.15);
      oscillator.start(now);
      oscillator.stop(now + 0.15);
      break;
    case 'wrong':
      oscillator.frequency.setValueAtTime(300, now);
      gainNode.gain.setValueAtTime(0.1, now);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.1);
      oscillator.start(now);
      oscillator.stop(now + 0.1);
      break;
  }
}

// ============================================
// SET DEFINITIONS
// ============================================

interface FlashcardSet {
  id: string;
  title: string;
  icon: string;
  category: string;
  startIndex?: number;
  endIndex?: number;
  // For category-based sets
  categoryFilter?: string;
  // For ID-based sets (Deutsch course)
  idPrefix?: string;
  idRange?: [number, number];
  // Task instruction shown above the card (e.g., "Was bedeutet dieses Adjektiv?")
  instruction?: string;
}

// Define sets based on course
function getFlashcardSets(courseId: string, flashcards: Flashcard[]): FlashcardSet[] {
  if (courseId === 'english-test3') {
    // Group by category and split large categories
    const unit6Cards = flashcards.filter(f => f.category === 'vocab-unit6');
    const australiaCards = flashcards.filter(f => f.category === 'vocab-australia');
    const unit7Cards = flashcards.filter(f => f.category === 'vocab-unit7');

    return [
      {
        id: 'unit6a',
        title: 'Unit 6A: Kidfluencer & Social Media',
        icon: '📱',
        category: 'vocab-unit6',
        startIndex: 0,
        endIndex: 23,
      },
      {
        id: 'unit6b',
        title: 'Unit 6B: Fast Fashion & Fairtrade',
        icon: '👗',
        category: 'vocab-unit6',
        startIndex: 23,
        endIndex: unit6Cards.length,
      },
      {
        id: 'australiaA',
        title: 'Australia A: Nature & Travel',
        icon: '🦘',
        category: 'vocab-australia',
        startIndex: 0,
        endIndex: 17,
      },
      {
        id: 'australiaB',
        title: 'Australia B: People & Outback',
        icon: '🏜️',
        category: 'vocab-australia',
        startIndex: 17,
        endIndex: australiaCards.length,
      },
      {
        id: 'unit7a',
        title: 'Unit 7A: Trends & Fashion',
        icon: '✨',
        category: 'vocab-unit7',
        startIndex: 0,
        endIndex: 22,
      },
      {
        id: 'unit7b',
        title: 'Unit 7B: Body Art & Tattoos',
        icon: '🎨',
        category: 'vocab-unit7',
        startIndex: 22,
        endIndex: unit7Cards.length,
      },
    ];
  }

  if (courseId === 'deutsch-schularbeit') {
    // Deutsch course - 7 distinct sets based on card ID ranges
    // Each set has a specific type of question (meaning, opposite, noun form, etc.)
    return [
      {
        id: 'de-bedeutungen',
        title: 'Adjektiv-Bedeutungen',
        icon: '📖',
        category: 'adjektive',
        idPrefix: 'de-fc-0',  // de-fc-01 to de-fc-05
        idRange: [1, 5],
        instruction: 'Was bedeutet dieses Adjektiv?',
      },
      {
        id: 'de-gegensatz',
        title: 'Gegensatzpaare',
        icon: '↔️',
        category: 'adjektive',
        idPrefix: 'de-fc-',
        idRange: [6, 15],
        instruction: 'Was ist das Gegenteil?',
      },
      {
        id: 'de-nomen',
        title: 'Adjektiv → Nomen',
        icon: '📝',
        category: 'adjektive',
        idPrefix: 'de-fc-',
        idRange: [16, 23],
        instruction: 'Bilde das Nomen zu diesem Adjektiv',
      },
      {
        id: 'de-modalverben',
        title: 'Modalverben',
        icon: '🎯',
        category: 'modifizieren',
        idPrefix: 'de-fc-',
        idRange: [24, 29],
        instruction: 'Welches Modalverb passt?',
      },
      {
        id: 'de-sprachmittel',
        title: 'Sprachliche Mittel',
        icon: '🗣️',
        category: 'modifizieren',
        idPrefix: 'de-fc-',
        idRange: [30, 33],
        instruction: 'Was gehört zu diesem sprachlichen Mittel?',
      },
      {
        id: 'de-indirekt',
        title: 'Indirekte Rede',
        icon: '💬',
        category: 'indirekt',
        idPrefix: 'de-fc-',
        idRange: [34, 36],
        instruction: 'Wie bildet man die indirekte Rede?',
      },
      {
        id: 'de-dasdass',
        title: 'das/dass Regeln',
        icon: '✍️',
        category: 'dasdass',
        idPrefix: 'de-fc-',
        idRange: [37, 39],
        instruction: 'Erkläre die Regel',
      },
      {
        id: 'de-metaphern-alltag',
        title: 'Alltagsmetaphern',
        icon: '🎭',
        category: 'metaphern',
        idPrefix: 'de-fc-',
        idRange: [40, 47],
        instruction: 'Was bedeutet diese Metapher?',
      },
      {
        id: 'de-metaphern-woerterbuch',
        title: 'Wörterbuch-Metaphern',
        icon: '📚',
        category: 'metaphern',
        idPrefix: 'de-fc-',
        idRange: [48, 55],
        instruction: 'Was bedeutet diese Metapher?',
      },
      {
        id: 'de-redewendungen',
        title: 'Redewendungen',
        icon: '💬',
        category: 'metaphern',
        idPrefix: 'de-fc-',
        idRange: [56, 62],
        instruction: 'Was bedeutet diese Redewendung?',
      },
    ] as FlashcardSet[];
  }

  if (courseId === 'spanisch-schularbeit') {
    return [
      {
        id: 'es-raeume',
        title: 'Modul 1: Schulräume & Orte',
        icon: '🏫',
        category: 'vocab-raeume',
        categoryFilter: 'vocab-raeume',
      },
      {
        id: 'es-schulsachen',
        title: 'Modul 2: Schulsachen',
        icon: '✏️',
        category: 'vocab-schulsachen',
        categoryFilter: 'vocab-schulsachen',
      },
      {
        id: 'es-berufe',
        title: 'Modul 3: Personen & Berufe',
        icon: '👥',
        category: 'vocab-berufe',
        categoryFilter: 'vocab-berufe',
      },
      {
        id: 'es-schule',
        title: 'Modul 4: Wochentage & Schulfächer',
        icon: '📅',
        category: 'vocab-schule',
        categoryFilter: 'vocab-schule',
      },
      {
        id: 'es-ausdruecke',
        title: 'Modul 5: Verben & Ausdrücke',
        icon: '💬',
        category: 'vocab-ausdruecke',
        categoryFilter: 'vocab-ausdruecke',
      },
    ] as FlashcardSet[];
  }

  // EU Geographie - one set per category
  const categories = Array.from(new Set(flashcards.map(f => f.category)));
  const categoryMeta: Record<string, { icon: string; title: string }> = {
    grundsaetze: { icon: '📋', title: 'Grundsaetze' },
    vorteile: { icon: '✅', title: 'Vorteile' },
    nachteile: { icon: '❌', title: 'Nachteile' },
    mitglieder: { icon: '🗺️', title: 'Mitglieder' },
    aufgaben: { icon: '📝', title: 'Aufgaben' },
    laender: { icon: '🏛️', title: 'Laender' },
    abkuerzungen: { icon: '🔤', title: 'Abkuerzungen' },
  };

  return categories.map(cat => ({
    id: cat,
    title: categoryMeta[cat]?.title || cat,
    icon: categoryMeta[cat]?.icon || '📚',
    category: cat,
    categoryFilter: cat,
  }));
}

function getCardsForSet(set: FlashcardSet, flashcards: Flashcard[]): Flashcard[] {
  let cards: Flashcard[];

  // ID range-based filtering (Deutsch course)
  if (set.idRange) {
    const [start, end] = set.idRange;
    cards = flashcards.filter(f => {
      // Extract number from ID (e.g., "de-fc-01" -> 1, "de-fc-15" -> 15)
      const match = f.id.match(/de-fc-(\d+)/);
      if (match) {
        const num = parseInt(match[1], 10);
        return num >= start && num <= end;
      }
      return false;
    });
  } else if (set.categoryFilter) {
    cards = flashcards.filter(f => f.category === set.categoryFilter);
  } else {
    const categoryCards = flashcards.filter(f => f.category === set.category);
    cards = categoryCards.slice(set.startIndex || 0, set.endIndex);
  }

  return cards;
}

// ============================================
// SHUFFLE HELPER
// ============================================

function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// ============================================
// SET SELECTOR COMPONENT
// ============================================

interface SetSelectorProps {
  sets: FlashcardSet[];
  flashcards: Flashcard[];
  progressMap: Map<string, boolean>;
  onSelectSet: (set: FlashcardSet | 'all' | 'weakness') => void;
  weaknessCount: number;
  courseId: string;
}

function SetSelector({ sets, flashcards, progressMap, onSelectSet, weaknessCount, courseId }: SetSelectorProps) {
  const [storyLoading, setStoryLoading] = useState(false);
  const [story, setStory] = useState<string | null>(null);

  const weaknessCards = useMemo(() => {
    return flashcards.filter(card => progressMap.get(card.id) === false);
  }, [flashcards, progressMap]);

  const generateStory = async () => {
    if (weaknessCards.length === 0) return;
    setStoryLoading(true);
    setStory(null);
    try {
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const words = weaknessCards.map(card => ({
        word: card.question,
        meaning: card.answer,
      }));
      const response = await fetch(`${supabaseUrl}/functions/v1/generate-story`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ words }),
      });
      const data = await response.json();
      if (data.story) {
        setStory(data.story);
      }
    } catch (err) {
      console.error('Story generation error:', err);
    } finally {
      setStoryLoading(false);
    }
  };

  // Calculate progress for each set
  const setProgress = useMemo(() => {
    const progress: Record<string, { total: number; known: number }> = {};

    for (const set of sets) {
      const cards = getCardsForSet(set, flashcards);
      let known = 0;
      for (const card of cards) {
        if (progressMap.get(card.id) === true) {
          known++;
        }
      }
      progress[set.id] = { total: cards.length, known };
    }

    return progress;
  }, [sets, flashcards, progressMap]);

  return (
    <div style={{ padding: '16px', paddingBottom: '100px' }}>
      <h2 style={{ color: '#ffd54f', fontSize: '20px', marginBottom: '8px' }}>
        Lernkarten-Sets
      </h2>
      <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', marginBottom: '20px' }}>
        Waehle ein Set zum Ueben
      </p>

      {/* Special options */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
        <button
          onClick={() => onSelectSet('all')}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '14px',
            background: 'rgba(66, 165, 245, 0.15)',
            border: '1px solid rgba(66, 165, 245, 0.3)',
            borderRadius: '12px',
            color: '#42a5f5',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <Shuffle size={18} />
          Alle mischen
        </button>

        <button
          onClick={() => onSelectSet('weakness')}
          disabled={weaknessCount === 0}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '14px',
            background: weaknessCount > 0
              ? 'rgba(255, 152, 0, 0.15)'
              : 'rgba(255,255,255,0.05)',
            border: weaknessCount > 0
              ? '1px solid rgba(255, 152, 0, 0.3)'
              : '1px solid rgba(255,255,255,0.1)',
            borderRadius: '12px',
            color: weaknessCount > 0 ? '#ff9800' : 'rgba(255,255,255,0.3)',
            fontSize: '14px',
            fontWeight: 600,
            cursor: weaknessCount > 0 ? 'pointer' : 'default',
            opacity: weaknessCount > 0 ? 1 : 0.5,
          }}
        >
          <AlertTriangle size={18} />
          Schwaechen ({weaknessCount})
        </button>
      </div>

      {/* Generate story from weaknesses */}
      {weaknessCount > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <button
            onClick={generateStory}
            disabled={storyLoading}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '14px',
              background: 'rgba(156, 39, 176, 0.15)',
              border: '1px solid rgba(156, 39, 176, 0.3)',
              borderRadius: '12px',
              color: '#ce93d8',
              fontSize: '14px',
              fontWeight: 600,
              cursor: storyLoading ? 'wait' : 'pointer',
            }}
          >
            {storyLoading ? '✍️ Geschichte wird geschrieben...' : '✨ Lerntext aus Schwaechen erstellen'}
          </button>
        </div>
      )}

      {/* Story display */}
      {story && (
        <div style={{
          marginBottom: '20px',
          padding: '20px',
          background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.1), rgba(103, 58, 183, 0.08))',
          border: '1px solid rgba(156, 39, 176, 0.25)',
          borderRadius: '16px',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '12px',
          }}>
            <div style={{ color: '#ce93d8', fontSize: '14px', fontWeight: 700 }}>
              📖 Deine Lerngeschichte
            </div>
            <button
              onClick={() => setStory(null)}
              style={{
                background: 'none',
                border: 'none',
                color: 'rgba(255,255,255,0.4)',
                cursor: 'pointer',
                fontSize: '18px',
              }}
            >
              ✕
            </button>
          </div>
          <style dangerouslySetInnerHTML={{ __html: `
            .story-word {
              color: #ffd54f;
              font-weight: 700;
              position: relative;
              cursor: help;
              border-bottom: 1px dotted rgba(255, 213, 79, 0.4);
            }
            .story-word:hover {
              color: #fff;
              background: rgba(255, 213, 79, 0.2);
              border-radius: 4px;
            }
            .story-word .story-tooltip {
              display: none;
              position: absolute;
              bottom: calc(100% + 8px);
              left: 50%;
              transform: translateX(-50%);
              background: #1a1a2e;
              border: 1px solid rgba(255, 213, 79, 0.4);
              border-radius: 10px;
              padding: 10px 14px;
              min-width: 200px;
              max-width: 280px;
              z-index: 100;
              box-shadow: 0 4px 20px rgba(0,0,0,0.5);
            }
            .story-word:hover .story-tooltip {
              display: block;
            }
            .story-tooltip-word {
              color: #ffd54f;
              font-size: 13px;
              font-weight: 700;
              margin-bottom: 4px;
            }
            .story-tooltip-meaning {
              color: #e8eaf6;
              font-size: 12px;
              font-weight: 400;
              line-height: 1.4;
              font-style: normal;
            }
          `}} />
          <div style={{
            color: '#e8eaf6',
            fontSize: '15px',
            lineHeight: 1.8,
            whiteSpace: 'pre-wrap',
          }}
            dangerouslySetInnerHTML={{
              __html: (() => {
                // Step 1: Strip all AI-generated ** markers
                const plainStory = story.replace(/\*\*([^*]+)\*\*/g, '$1');

                // Step 2: Build a map of weakness flashcard words → full meaning
                const wordMap = new Map<string, { question: string; answer: string }>();
                for (const card of weaknessCards) {
                  wordMap.set(card.question.toLowerCase(), { question: card.question, answer: card.answer });
                }

                // Step 3: Sort by length (longest first) to match multi-word phrases before single words
                const sortedWords = Array.from(wordMap.keys()).sort((a, b) => b.length - a.length);
                if (sortedWords.length === 0) return plainStory;

                // Step 4: Build regex that matches only exact flashcard words (word boundaries)
                const escaped = sortedWords.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
                const pattern = new RegExp(`\\b(${escaped.join('|')})\\b`, 'gi');

                // Step 5: Replace only exact flashcard matches with tooltip spans
                return plainStory.replace(pattern, (matched) => {
                  const entry = wordMap.get(matched.toLowerCase());
                  if (!entry) return matched;
                  const fullMeaning = entry.answer;
                  return `<span class="story-word">${matched}<span class="story-tooltip"><div class="story-tooltip-word">${entry.question}</div><div class="story-tooltip-meaning">${fullMeaning}</div></span></span>`;
                });
              })()
            }}
          />
        </div>
      )}

      {/* Set cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {sets.map(set => {
          const prog = setProgress[set.id] || { total: 0, known: 0 };
          const pct = prog.total > 0 ? Math.round((prog.known / prog.total) * 100) : 0;

          return (
            <button
              key={set.id}
              onClick={() => onSelectSet(set)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '12px',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <span style={{ fontSize: '28px' }}>{set.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ color: '#e8eaf6', fontSize: '15px', fontWeight: 600 }}>
                  {set.title}
                </div>
                <div style={{
                  color: 'rgba(255,255,255,0.5)',
                  fontSize: '12px',
                  marginTop: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                  <span>{prog.total} Karten</span>
                  <span>•</span>
                  <span style={{ color: pct === 100 ? '#4caf50' : '#ffd54f' }}>
                    {prog.known}/{prog.total} gewusst
                  </span>
                </div>
                {/* Progress bar */}
                <div style={{
                  marginTop: '8px',
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '4px',
                  height: '4px',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    background: pct === 100 ? '#4caf50' : '#ffd54f',
                    height: '100%',
                    width: `${pct}%`,
                    transition: 'width 0.3s',
                  }} />
                </div>
              </div>
              <ChevronRight size={20} style={{ color: 'rgba(255,255,255,0.4)' }} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============================================
// FLASHCARD GAME COMPONENT
// ============================================

interface FlashcardGameProps {
  cards: Flashcard[];
  courseId: string;
  setTitle: string;
  instruction?: string;
  onBack: () => void;
  onComplete: (knew: number, total: number) => void;
  recordResult: (cardId: string, knew: boolean) => Promise<void>;
  isWeaknessMode?: boolean;
}

function FlashcardGame({
  cards,
  courseId,
  setTitle,
  instruction,
  onBack,
  onComplete,
  recordResult,
  isWeaknessMode,
}: FlashcardGameProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [isChecked, setIsChecked] = useState(false);
  const [matchResult, setMatchResult] = useState<MatchResult | null>(null);
  const [lastCheckResult, setLastCheckResult] = useState<{
    normalizedInput: string;
    normalizedCorrect: string;
  } | null>(null);
  const [score, setScore] = useState({ knew: 0, didnt: 0 });
  const [aiChecking, setAiChecking] = useState(false);
  const [aiExplanation, setAiExplanation] = useState<string | null>(null);
  const [hintPlaying, setHintPlaying] = useState(false);
  const hintAudioRef = useRef<HTMLAudioElement | null>(null);
  const [hintAvailable, setHintAvailable] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const hintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (hintAudioRef.current) {
        hintAudioRef.current.pause();
        hintAudioRef.current = null;
      }
    };
  }, []);

  // Hint timer — show hint button after 10 seconds without submitting
  useEffect(() => {
    setHintAvailable(false);
    setShowHint(false);
    if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
    if (!isChecked) {
      hintTimerRef.current = setTimeout(() => setHintAvailable(true), 10000);
    }
    return () => { if (hintTimerRef.current) clearTimeout(hintTimerRef.current); };
  }, [currentIndex, isChecked]);

  // Generate hint text from correct answer
  const getHintText = () => {
    if (!currentCard) return '';
    const answer = getBack(currentCard);
    // Show first 30% of characters + ...
    const showChars = Math.max(2, Math.ceil(answer.length * 0.3));
    return answer.substring(0, showChars) + '...';
  };

  // Check if card is a vocab card (can be reversed)
  const isVocabCard = (card: Flashcard) => {
    // Vocab categories start with "vocab-"
    return card.category.startsWith('vocab-');
  };

  // Shuffle cards and assign random direction (50/50 EN→DE or DE→EN)
  const preparedCards = useMemo(() => {
    return shuffleArray(cards).map(card => ({
      ...card,
      // For vocab cards, randomly flip direction
      // For Q&A cards (EU Geo), always show question first
      isReversed: isVocabCard(card) && Math.random() > 0.5,
    }));
  }, [cards]);

  const currentCard = preparedCards[currentIndex];

  const getFront = (card: typeof currentCard) => {
    if (!card) return '';
    // For vocab cards, respect direction
    if (isVocabCard(card)) {
      return card.isReversed ? card.answer : card.question;
    }
    // For Q&A cards, always show question
    return card.question;
  };

  const getBack = (card: typeof currentCard) => {
    if (!card) return '';
    if (isVocabCard(card)) {
      return card.isReversed ? card.question : card.answer;
    }
    return card.answer;
  };

  // Look up instruction for a card based on which set it belongs to
  const getCardInstruction = useCallback((card: Flashcard | null): string | undefined => {
    if (!card || !courseId) return undefined;
    const allSets = getFlashcardSets(courseId, cards);
    for (const set of allSets) {
      if (set.instruction && set.idRange) {
        const match = card.id.match(/de-fc-(\d+)/);
        if (match) {
          const num = parseInt(match[1], 10);
          if (num >= set.idRange[0] && num <= set.idRange[1]) {
            return set.instruction;
          }
        }
      }
    }
    return undefined;
  }, [courseId, cards]);

  const getDirectionLabel = (card: typeof currentCard) => {
    if (!card || !isVocabCard(card)) {
      return instruction || getCardInstruction(card) || 'Frage';
    }
    if (courseId === 'deutsch-schularbeit') {
      return card.isReversed ? 'Antwort → Frage' : 'Frage → Antwort';
    }
    if (courseId === 'spanisch-schularbeit') {
      return card.isReversed ? 'Deutsch → Español' : 'Español → Deutsch';
    }
    return card.isReversed ? 'Deutsch → English' : 'English → Deutsch';
  };

  // Get example sentence with the English word masked
  // Example sentences are always in English, so we mask:
  // - EN→DE (not reversed): mask the front (English word shown)
  // - DE→EN (reversed): mask the back (English word to type)
  const getMaskedExample = (card: typeof currentCard): string | null => {
    if (!card || !card.speech) return null;

    const example = card.speech;

    // For vocab cards, always mask the English word (which appears in the example)
    // For EN→DE: front is English (question), for DE→EN: back is English (answer)
    const wordToMask = isVocabCard(card)
      ? (card.isReversed ? getBack(card) : getFront(card))
      : getBack(card);  // For non-vocab Q&A cards, mask the answer

    // Clean the word: remove "...", parentheses content, "to " prefix for matching
    let cleanWord = wordToMask
      .replace(/\.\.\./g, '')           // Remove "..."
      .replace(/\([^)]*\)/g, '')        // Remove (+ verb), (sth), etc.
      .replace(/\bsb\.?\b/gi, '')       // Remove "sb"
      .replace(/\bsth\.?\b/gi, '')      // Remove "sth"
      .trim();

    // Only mask the EXACT phrase, not individual words!
    // For multi-word phrases like "to dive into a world of fantasy",
    // we only mask the full phrase (or key verb), not each word separately
    const phrasesToMask: string[] = [];

    // If it starts with "to ", try to find the key verb/phrase
    if (cleanWord.toLowerCase().startsWith('to ')) {
      const withoutTo = cleanWord.slice(3).trim();
      // Get just the first 1-2 significant words (the actual verb/action)
      const verbWords = withoutTo.split(/\s+/).slice(0, 2);
      if (verbWords.length > 0) {
        phrasesToMask.push(verbWords[0]); // Just the main verb: "dive"
        if (verbWords.length > 1 && !['into', 'a', 'an', 'the', 'to', 'for', 'with'].includes(verbWords[1].toLowerCase())) {
          phrasesToMask.push(verbWords.slice(0, 2).join(' ')); // "dive into" only if 2nd word is significant
        }
      }
    } else {
      // For non-verb phrases, mask the whole phrase or key words
      const phraseWords = cleanWord.split(/\s+/);
      if (phraseWords.length <= 3) {
        // Short phrase: mask the whole thing
        phrasesToMask.push(cleanWord);
      } else {
        // Long phrase: just mask the first 2 significant words
        const significant = phraseWords.filter(w =>
          !['to', 'the', 'a', 'an', 'and', 'or', 'for', 'with', 'from', 'of', 'in', 'on'].includes(w.toLowerCase())
        );
        if (significant.length > 0) {
          phrasesToMask.push(significant[0]);
          if (significant.length > 1) {
            phrasesToMask.push(significant.slice(0, 2).join(' '));
          }
        }
      }
    }

    // Sort by length descending (mask longer phrases first)
    const uniquePhrases = Array.from(new Set(phrasesToMask)).sort((a, b) => b.length - a.length);

    let masked = example;

    for (const phrase of uniquePhrases) {
      if (phrase.length < 3) continue;

      // Escape regex special chars
      const escaped = phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

      // For verbs, also match conjugated forms (-s, -es, -ed, -ing, -d)
      const verbPattern = `\\b${escaped}(s|es|ed|ing|d)?\\b`;
      const regex = new RegExp(verbPattern, 'gi');

      if (regex.test(masked)) {
        masked = masked.replace(new RegExp(verbPattern, 'gi'), '___');
        break; // Stop after first successful mask to avoid over-masking
      }
    }

    // If nothing was masked, don't show (would reveal the answer)
    if (masked === example) {
      console.log('[Flashcard] Example hidden - nothing was masked');
      return null;
    }

    // Count visible words (excluding ___)
    const visibleWords = masked.split(/\s+/).filter(w => w !== '___' && w.length > 0);
    if (visibleWords.length < 4) {
      console.log('[Flashcard] Example hidden - too few visible words:', visibleWords.length);
      return null;
    }

    return masked;
  };

  const handleSubmit = useCallback(async () => {
    if (!userInput.trim() || isChecked) return;

    const correctAnswer = getBack(currentCard);
    const checkResult = checkAnswer(userInput, correctAnswer);

    setAiExplanation(null);

    // If string matching says "wrong", ask AI for a second opinion
    if (checkResult.result === 'wrong' && userInput.trim().length >= 2) {
      setAiChecking(true);
      setIsChecked(true);
      setMatchResult(null); // Show loading state
      setLastCheckResult({
        normalizedInput: checkResult.normalizedInput,
        normalizedCorrect: checkResult.normalizedCorrect,
      });

      try {
        const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
        const response = await fetch(`${supabaseUrl}/functions/v1/check-answer`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            question: getFront(currentCard),
            expectedAnswer: correctAnswer,
            userAnswer: userInput,
            instruction: instruction || setTitle,
          }),
        });

        const data = await response.json();

        if (data.fallback || data.error) {
          // AI failed, use string matching result
          setMatchResult(checkResult.result);
          playSound(checkResult.result);
          const knew = false;
          await recordResult(currentCard.id, knew);
          setScore(s => ({ ...s, didnt: s.didnt + 1 }));
        } else {
          // Use AI result
          const aiResult = data.result as MatchResult;
          setMatchResult(aiResult);
          setAiExplanation(data.explanation || null);
          playSound(aiResult);
          const knew = aiResult === 'correct' || aiResult === 'almost';
          await recordResult(currentCard.id, knew);
          if (knew) {
            setScore(s => ({ ...s, knew: s.knew + 1 }));
          } else {
            setScore(s => ({ ...s, didnt: s.didnt + 1 }));
          }
        }
      } catch {
        // Network error, fall back to string matching
        setMatchResult(checkResult.result);
        playSound(checkResult.result);
        await recordResult(currentCard.id, false);
        setScore(s => ({ ...s, didnt: s.didnt + 1 }));
      } finally {
        setAiChecking(false);
      }
      return;
    }

    // String matching was correct or almost — use directly
    setMatchResult(checkResult.result);
    setLastCheckResult({
      normalizedInput: checkResult.normalizedInput,
      normalizedCorrect: checkResult.normalizedCorrect,
    });
    setIsChecked(true);
    playSound(checkResult.result);

    const knew = checkResult.result === 'correct' || checkResult.result === 'almost';
    await recordResult(currentCard.id, knew);

    if (knew) {
      setScore(s => ({ ...s, knew: s.knew + 1 }));
    } else {
      setScore(s => ({ ...s, didnt: s.didnt + 1 }));
    }
  }, [userInput, isChecked, currentCard, recordResult, instruction, setTitle]);

  const stopHintAudio = useCallback(() => {
    if (hintAudioRef.current) {
      hintAudioRef.current.pause();
      hintAudioRef.current.currentTime = 0;
    }
    setHintPlaying(false);
  }, []);

  const playHintAudio = useCallback((src: string) => {
    stopHintAudio();
    // Reuse existing element or create once — iOS Safari requires
    // the play() call to originate from the same user tap and
    // works more reliably with a persistent <audio> element.
    let audio = hintAudioRef.current;
    if (!audio) {
      audio = document.createElement('audio');
      audio.setAttribute('playsinline', '');
      audio.preload = 'auto';
      hintAudioRef.current = audio;
    }
    audio.src = src;
    audio.onended = () => setHintPlaying(false);
    audio.onerror = () => setHintPlaying(false);
    setHintPlaying(true);
    const p = audio.play();
    if (p && p.catch) p.catch(() => setHintPlaying(false));
  }, [stopHintAudio]);

  const handleNext = useCallback(() => {
    stopHintAudio();
    if (currentIndex < preparedCards.length - 1) {
      setCurrentIndex(i => i + 1);
      setUserInput('');
      setIsChecked(false);
      setMatchResult(null);
      setLastCheckResult(null);
      setAiExplanation(null);
    } else {
      onComplete(score.knew, preparedCards.length);
    }
  }, [currentIndex, preparedCards.length, onComplete, score.knew, stopHintAudio]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (!isChecked) {
        handleSubmit();
      } else {
        handleNext();
      }
    }
  };

  if (!currentCard) {
    return null;
  }

  return (
    <div style={{ padding: '16px', paddingBottom: '100px' }}>
      {/* Header */}
      <button
        onClick={onBack}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          color: '#9fa8da',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          marginBottom: '16px',
          padding: 0,
          fontSize: '14px',
        }}
      >
        <ArrowLeft size={18} />
        Zurueck
      </button>

      {/* Progress */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
      }}>
        <div style={{ color: '#e8eaf6', fontSize: '14px' }}>
          Karte {currentIndex + 1} von {preparedCards.length}
          {isWeaknessMode && (
            <span style={{
              marginLeft: '8px',
              fontSize: '11px',
              color: '#ff9800',
              background: 'rgba(255, 152, 0, 0.2)',
              padding: '2px 8px',
              borderRadius: '6px',
            }}>
              Schwaechen
            </span>
          )}
        </div>
        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
          ✅ {score.knew} | ❌ {score.didnt}
        </div>
      </div>

      {/* Set title */}
      <div style={{
        color: '#ffd54f',
        fontSize: '14px',
        fontWeight: 600,
        marginBottom: '8px',
        textTransform: 'uppercase',
      }}>
        {setTitle}
      </div>

      {/* Card front */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(255, 213, 79, 0.1), rgba(255, 152, 0, 0.05))',
        border: '1px solid rgba(255, 213, 79, 0.3)',
        borderRadius: '16px',
        padding: '24px 20px',
        marginBottom: '16px',
        minHeight: '120px',
      }}>
        <div style={{
          color: '#ffd54f',
          fontSize: '13px',
          marginBottom: '12px',
          textTransform: 'uppercase',
          fontWeight: 600,
        }}>
          {getDirectionLabel(currentCard)}
        </div>
        <div style={{
          color: '#e8eaf6',
          fontSize: '20px',
          fontWeight: 600,
          lineHeight: 1.4,
        }}>
          {getFront(currentCard)}
        </div>
        {/* Example sentence with masked target word */}
        {getMaskedExample(currentCard) && (
          <div style={{
            marginTop: '16px',
            color: 'rgba(255,255,255,0.5)',
            fontSize: '14px',
            fontStyle: 'italic',
            lineHeight: 1.5,
          }}>
            ({getMaskedExample(currentCard)})
          </div>
        )}
      </div>

      {/* Input area (before check) */}
      {!isChecked && (
        <div style={{ marginBottom: '16px' }}>
          <input
            type="text"
            value={userInput}
            onChange={e => setUserInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Deine Antwort..."
            autoFocus
            style={{
              width: '100%',
              padding: '16px',
              fontSize: '16px',
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '12px',
              color: '#e8eaf6',
              outline: 'none',
              marginBottom: '12px',
            }}
          />
          {courseId === 'mathe-schularbeit' && (
            <MathKeyboard
              onInsert={(symbol) => setUserInput(prev => prev + symbol)}
              onTransfer={(value) => setUserInput(value)}
            />
          )}
          {/* Hint button — appears after 10 seconds */}
          {hintAvailable && !showHint && (
            <button
              onClick={() => setShowHint(true)}
              style={{
                width: '100%',
                padding: '10px',
                marginBottom: '8px',
                background: 'rgba(255, 152, 0, 0.1)',
                border: '1px solid rgba(255, 152, 0, 0.3)',
                borderRadius: '10px',
                color: '#ff9800',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              💡 Tipp anzeigen
            </button>
          )}
          {showHint && (
            <div style={{
              padding: '10px 14px',
              marginBottom: '8px',
              background: 'rgba(255, 152, 0, 0.08)',
              border: '1px solid rgba(255, 152, 0, 0.2)',
              borderRadius: '10px',
              color: '#ff9800',
              fontSize: '14px',
            }}>
              💡 <strong>Tipp:</strong> {getHintText()}
            </div>
          )}
          <button
            onClick={handleSubmit}
            disabled={!userInput.trim()}
            style={{
              width: '100%',
              padding: '16px',
              background: userInput.trim() ? '#ffd54f' : 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: '12px',
              color: userInput.trim() ? '#1a1a2e' : 'rgba(255,255,255,0.3)',
              fontSize: '16px',
              fontWeight: 700,
              cursor: userInput.trim() ? 'pointer' : 'default',
            }}
          >
            Pruefen
          </button>
        </div>
      )}

      {/* Result area (after check) */}
      {isChecked && (
        <div style={{ marginBottom: '16px' }}>
          {/* AI loading state */}
          {aiChecking && (
            <div style={{
              padding: '16px',
              borderRadius: '12px',
              marginBottom: '12px',
              background: 'rgba(66, 165, 245, 0.15)',
              border: '1px solid rgba(66, 165, 245, 0.3)',
              textAlign: 'center',
            }}>
              <div style={{ color: '#42a5f5', fontSize: '16px', fontWeight: 600 }}>
                Wird geprueft...
              </div>
            </div>
          )}

          {/* Feedback message */}
          {!aiChecking && matchResult && (
          <div style={{
            padding: '16px',
            borderRadius: '12px',
            marginBottom: '12px',
            background: matchResult === 'correct'
              ? 'rgba(76, 175, 80, 0.15)'
              : matchResult === 'almost'
                ? 'rgba(76, 175, 80, 0.12)'
                : 'rgba(239, 83, 80, 0.15)',
            border: `1px solid ${
              matchResult === 'correct'
                ? 'rgba(76, 175, 80, 0.3)'
                : matchResult === 'almost'
                  ? 'rgba(76, 175, 80, 0.25)'
                  : 'rgba(239, 83, 80, 0.3)'
            }`,
          }}>
            <div style={{
              color: matchResult === 'correct'
                ? '#4caf50'
                : matchResult === 'almost'
                  ? '#8bc34a'
                  : '#ef5350',
              fontSize: '18px',
              fontWeight: 700,
              marginBottom: '8px',
            }}>
              {matchResult === 'correct' && '✅ Super! 👏'}
              {matchResult === 'almost' && '✅ Stimmt!'}
              {matchResult === 'wrong' && '❌ Nicht ganz.'}
            </div>

            {/* Always show what the user typed */}
            <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', marginBottom: '8px' }}>
              <span style={{ color: 'rgba(255,255,255,0.4)' }}>Du hast: </span>
              <span style={{
                color: matchResult === 'correct' ? '#4caf50' :
                       matchResult === 'almost' ? '#8bc34a' : '#e8eaf6',
              }}>
                {userInput}
              </span>
            </div>

            {/* AI explanation */}
            {aiExplanation && (
              <div style={{
                color: 'rgba(255,255,255,0.7)',
                fontSize: '13px',
                marginBottom: '8px',
                fontStyle: 'italic',
              }}>
                {aiExplanation}
              </div>
            )}

            {matchResult === 'almost' && (
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>
                <span style={{ color: 'rgba(255,255,255,0.5)' }}>Vollstaendige Antwort: </span>
                <span style={{ color: '#4caf50' }}>{getBack(currentCard)}</span>
              </div>
            )}

            {matchResult === 'wrong' && (
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>
                <span style={{ color: 'rgba(255,255,255,0.5)' }}>Richtig waere: </span>
                <span style={{ color: '#4caf50' }}>{getBack(currentCard)}</span>
              </div>
            )}

            {matchResult === 'wrong' && currentCard.hintAudio && (
              <button
                onClick={() => hintPlaying ? stopHintAudio() : playHintAudio(currentCard.hintAudio!)}
                style={{
                  marginTop: '10px',
                  padding: '10px 16px',
                  background: hintPlaying ? 'rgba(255,167,38,0.15)' : 'rgba(66,165,245,0.12)',
                  border: `1px solid ${hintPlaying ? 'rgba(255,167,38,0.4)' : 'rgba(66,165,245,0.3)'}`,
                  borderRadius: '10px',
                  color: hintPlaying ? '#ffa726' : '#64b5f6',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                {hintPlaying ? '⏸ Läuft...' : '🎧 Eselsbrücke anhören'}
              </button>
            )}

            {matchResult === 'correct' && (
              <div style={{ color: '#4caf50', fontSize: '14px' }}>
                ✓ {getBack(currentCard)}
              </div>
            )}
          </div>
          )}

          {/* Next button */}
          <button
            onClick={handleNext}
            style={{
              width: '100%',
              padding: '16px',
              background: '#42a5f5',
              border: 'none',
              borderRadius: '12px',
              color: '#fff',
              fontSize: '16px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {currentIndex < preparedCards.length - 1 ? 'Weiter' : 'Ergebnis anzeigen'}
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================
// RESULT SCREEN
// ============================================

interface ResultScreenProps {
  knew: number;
  total: number;
  onRestart: () => void;
  onBack: () => void;
  courseId: string;
  course: ReturnType<typeof useCourse>['course'];
  userId?: string;
  category?: string;
}

function ResultScreen({ knew, total, onRestart, onBack, courseId, course, userId, category }: ResultScreenProps) {
  const percentage = Math.round((knew / total) * 100);
  const [recommendation, setRecommendation] = useState<ReturnType<typeof getNextRecommendation> | null>(null);

  useEffect(() => {
    async function loadRecommendation() {
      if (!userId || !course) return;

      const progress = await getCourseProgress(userId, courseId, course);
      const rec = getNextRecommendation(courseId, progress, course, {
        justCompleted: 'flashcards',
        lastScore: percentage,
        category: category || undefined,
      });
      setRecommendation(rec);
    }

    loadRecommendation();
  }, [userId, courseId, course, percentage, category]);

  return (
    <div style={{
      padding: '16px',
      paddingBottom: '100px',
      textAlign: 'center',
    }}>
      <div style={{ fontSize: '64px', marginBottom: '16px' }}>
        {percentage >= 80 ? '🎉' : percentage >= 50 ? '👍' : '💪'}
      </div>
      <h1 style={{ color: '#ffd54f', fontSize: '28px', marginBottom: '8px' }}>
        Fertig!
      </h1>
      <div style={{
        color: '#e8eaf6',
        fontSize: '48px',
        fontWeight: 700,
        marginBottom: '8px',
      }}>
        {knew}/{total}
      </div>
      <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '18px', marginBottom: '32px' }}>
        {percentage}% gewusst
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <button
          onClick={onRestart}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '16px',
            background: '#ffd54f',
            border: 'none',
            borderRadius: '12px',
            color: '#1a1a2e',
            fontSize: '16px',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          <RotateCcw size={20} />
          Nochmal
        </button>
        <button
          onClick={onBack}
          style={{
            padding: '16px',
            background: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '12px',
            color: '#e8eaf6',
            fontSize: '16px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Andere Sets
        </button>
      </div>

      {/* Recommendation */}
      {recommendation && (
        <RecommendationCard recommendation={recommendation} />
      )}
    </div>
  );
}

// ============================================
// MAIN FLASHCARDS COMPONENT
// ============================================

export function Flashcards() {
  const { courseId } = useParams<{ courseId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { course, isLoading } = useCourse(courseId || '');
  const { user } = useAuth();
  const { recordFlashcardResult } = useProgress(user?.id, courseId || '');

  const [selectedSet, setSelectedSet] = useState<FlashcardSet | 'all' | 'weakness' | null>(null);
  const [progressMap, setProgressMap] = useState<Map<string, boolean>>(new Map());
  const [progressLoaded, setProgressLoaded] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [finalScore, setFinalScore] = useState({ knew: 0, total: 0 });
  const [sessionKey, setSessionKey] = useState(0);
  const [startTime] = useState(Date.now());

  // Load flashcard progress
  useEffect(() => {
    async function loadProgress() {
      if (!user?.id || !courseId) {
        setProgressLoaded(true);
        return;
      }

      const progress = await getFlashcardProgress(user.id, courseId);
      const map = new Map<string, boolean>();
      progress.forEach((p: FlashcardProgress) => {
        if (!p.flashcard_id.startsWith('vocab_')) {
          map.set(p.flashcard_id, p.knew);
        }
      });
      setProgressMap(map);
      setProgressLoaded(true);
    }

    loadProgress();
  }, [user?.id, courseId]);

  const categoryFilter = searchParams.get('category');
  const categoryLabel = categoryFilter && course?.categories
    ? course.categories.find(c => c.id === categoryFilter)?.label || categoryFilter
    : null;
  const flashcards = course?.flashcards || [];
  const allSets = useMemo(() => getFlashcardSets(courseId || '', flashcards), [courseId, flashcards]);
  const sets = useMemo(() => {
    if (!categoryFilter) return allSets;
    return allSets.filter(s => s.category === categoryFilter);
  }, [allSets, categoryFilter]);

  // Count weakness cards — filtered by category if active
  const weaknessCards = useMemo(() => {
    const pool = categoryFilter
      ? flashcards.filter(f => f.category === categoryFilter)
      : flashcards;
    return pool.filter(card => progressMap.get(card.id) === false);
  }, [flashcards, progressMap, categoryFilter]);

  // Auto-start weakness mode if ?mode=weakness in URL
  // Also handles same-page navigation from result screen "Jetzt machen" button
  useEffect(() => {
    if (searchParams.get('mode') !== 'weakness' || !progressLoaded) return;

    if (isFinished) {
      // Coming from result screen — reset state and reload fresh progress
      setIsFinished(false);
      setFinalScore({ knew: 0, total: 0 });
      setSessionKey(k => k + 1);
      setSelectedSet(null);
      if (user?.id && courseId) {
        getFlashcardProgress(user.id, courseId).then(progress => {
          const map = new Map<string, boolean>();
          progress.forEach((p: FlashcardProgress) => {
            if (!p.flashcard_id.startsWith('vocab_')) {
              map.set(p.flashcard_id, p.knew);
            }
          });
          setProgressMap(map);
        });
      }
      return;
    }

    // Normal auto-start (first load or after progress reload)
    if (!selectedSet && weaknessCards.length > 0) {
      setSelectedSet('weakness');
    }
  }, [searchParams, progressLoaded, weaknessCards.length, selectedSet, isFinished]);

  // Get cards for current selection
  const currentCards = useMemo(() => {
    if (!selectedSet) return [];
    if (selectedSet === 'all') {
      return categoryFilter
        ? flashcards.filter(f => f.category === categoryFilter)
        : flashcards;
    }
    if (selectedSet === 'weakness') return weaknessCards;
    return getCardsForSet(selectedSet, flashcards);
  }, [selectedSet, flashcards, weaknessCards]);

  const handleComplete = async (knew: number, total: number) => {
    setFinalScore({ knew, total });
    setIsFinished(true);

    // Log activity
    if (user?.id && courseId) {
      const elapsedMinutes = Math.ceil((Date.now() - startTime) / 60000);
      await logActivity(user.id, 'flashcards', {
        course_id: courseId,
        cards_reviewed: total,
        cards_known: knew,
        cards_unknown: total - knew,
        duration_minutes: elapsedMinutes,
        set_id: selectedSet && typeof selectedSet === 'object' ? selectedSet.id : String(selectedSet),
        is_weakness_mode: selectedSet === 'weakness',
      });
    }
  };

  const handleRestart = () => {
    setIsFinished(false);
    setFinalScore({ knew: 0, total: 0 });
    setSessionKey(k => k + 1); // Force FlashcardGame re-mount to reset all internal state
  };

  const handleBackToSets = async () => {
    setSelectedSet(null);
    setIsFinished(false);
    setFinalScore({ knew: 0, total: 0 });

    // Clear mode=weakness from URL to prevent useEffect from re-selecting weakness
    if (searchParams.get('mode')) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('mode');
      setSearchParams(newParams, { replace: true });
    }

    // Reload progress from DB so set selector shows updated counts
    if (user?.id && courseId) {
      const progress = await getFlashcardProgress(user.id, courseId);
      const map = new Map<string, boolean>();
      progress.forEach((p: FlashcardProgress) => {
        if (!p.flashcard_id.startsWith('vocab_')) {
          map.set(p.flashcard_id, p.knew);
        }
      });
      setProgressMap(map);
    }
  };

  if (isLoading || !course || !progressLoaded) {
    return <div className="loading">Laden...</div>;
  }

  if (flashcards.length === 0) {
    return (
      <div className="flashcards-view">
        <Link to={`/course/${courseId}`} className="back-link">
          <ArrowLeft size={18} />
          Zurueck
        </Link>
        <p style={{ color: 'rgba(255,255,255,0.6)', textAlign: 'center', marginTop: '40px' }}>
          Keine Lernkarten verfuegbar.
        </p>
      </div>
    );
  }

  // Show result screen
  if (isFinished) {
    return (
      <div>
        <Link
          to={`/course/${courseId}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            color: '#9fa8da',
            textDecoration: 'none',
            padding: '16px',
          }}
        >
          <ArrowLeft size={18} />
          Zurueck zum Kurs
        </Link>
        {categoryLabel && (
          <div style={{ padding: '0 16px 8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>Thema:</span>
            <span style={{ fontSize: '13px', padding: '2px 10px', borderRadius: '8px', background: 'rgba(255, 213, 79, 0.12)', border: '1px solid rgba(255, 213, 79, 0.25)', color: '#ffd54f', fontWeight: 600 }}>{categoryLabel}</span>
          </div>
        )}
        <ResultScreen
          knew={finalScore.knew}
          total={finalScore.total}
          onRestart={handleRestart}
          onBack={handleBackToSets}
          courseId={courseId || ''}
          course={course}
          userId={user?.id}
          category={categoryFilter || undefined}
        />
      </div>
    );
  }

  // Show game
  if (selectedSet && currentCards.length > 0) {
    const setTitle = typeof selectedSet === 'object'
      ? selectedSet.title
      : selectedSet === 'all'
        ? 'Alle Karten'
        : 'Schwaechen ueben';

    return (
      <div>
        <Link
          to={`/course/${courseId}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            color: '#9fa8da',
            textDecoration: 'none',
            padding: '16px',
          }}
        >
          <ArrowLeft size={18} />
          Zurueck zum Kurs
        </Link>
        {categoryLabel && (
          <div style={{ padding: '0 16px 8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>Thema:</span>
            <span style={{ fontSize: '13px', padding: '2px 10px', borderRadius: '8px', background: 'rgba(255, 213, 79, 0.12)', border: '1px solid rgba(255, 213, 79, 0.25)', color: '#ffd54f', fontWeight: 600 }}>{categoryLabel}</span>
          </div>
        )}
        <FlashcardGame
          key={sessionKey}
          cards={currentCards}
          courseId={courseId || ''}
          setTitle={setTitle}
          instruction={typeof selectedSet === 'object' ? selectedSet.instruction : undefined}
          onBack={handleBackToSets}
          onComplete={handleComplete}
          recordResult={recordFlashcardResult}
          isWeaknessMode={selectedSet === 'weakness'}
        />
      </div>
    );
  }

  // Show set selector
  return (
    <div>
      <Link
        to={`/course/${courseId}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          color: '#9fa8da',
          textDecoration: 'none',
          padding: '16px',
        }}
      >
        <ArrowLeft size={18} />
        Zurueck zum Kurs
      </Link>
      {categoryLabel && (
        <div style={{ padding: '0 16px 8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>Thema:</span>
          <span style={{ fontSize: '13px', padding: '2px 10px', borderRadius: '8px', background: 'rgba(255, 213, 79, 0.12)', border: '1px solid rgba(255, 213, 79, 0.25)', color: '#ffd54f', fontWeight: 600 }}>{categoryLabel}</span>
        </div>
      )}
      <SetSelector
        sets={sets}
        flashcards={flashcards}
        progressMap={progressMap}
        onSelectSet={setSelectedSet}
        weaknessCount={weaknessCards.length}
        courseId={courseId || ''}
      />
    </div>
  );
}
